/**
 * PortalShared.jsx
 * Shared components for all 4 partner command centers.
 * Eliminates ~600 lines of duplicated sidebar + utility code.
 */

import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, ArrowRight, Globe, LayoutDashboard, RefreshCw, Home, Landmark, HardHat, Briefcase } from "lucide-react";
import DealForgeLogo, { DealForgeIcon } from "./DealForgeLogo";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Portal role config for switcher
const PORTAL_SWITCHER_ROLES = {
  lender:     { label: "Lender",     icon: Landmark,  path: "/lender/portal",       color: "text-blue-400",    border: "border-blue-500/30",    bg: "bg-blue-500/10" },
  realtor:    { label: "Realtor",    icon: Home,       path: "/realtor/dashboard",   color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  contractor: { label: "Contractor", icon: HardHat,    path: "/contractor/dashboard",color: "text-orange-400",  border: "border-orange-500/30",  bg: "bg-orange-500/10" },
  title:      { label: "Title Co.",  icon: Briefcase,  path: "/title/dashboard",     color: "text-purple-400",  border: "border-purple-500/30",  bg: "bg-purple-500/10" },
};

// ── Portal accent theme map ───────────────────────────────────────────────────
const ACCENT = {
  gold: {
    activeNav:   "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]",
    primaryBg:   "bg-[#D4AF37]/[0.08] border-[#D4AF37]/30 hover:bg-[#D4AF37]/[0.15]",
    primaryIcon: "bg-[#D4AF37]/20",
    primaryText: "text-[#D4AF37]",
  },
  purple: {
    activeNav:   "bg-purple-500/10 text-[#BF5AF2] border-l-2 border-purple-500",
    primaryBg:   "bg-purple-500/[0.08] border-purple-500/30 hover:bg-purple-500/[0.15]",
    primaryIcon: "bg-purple-500/20",
    primaryText: "text-[#BF5AF2]",
  },
  emerald: {
    activeNav:   "bg-emerald-500/10 text-[#00FF88] border-l-2 border-emerald-500",
    primaryBg:   "bg-emerald-500/[0.08] border-emerald-500/30 hover:bg-emerald-500/[0.15]",
    primaryIcon: "bg-emerald-500/20",
    primaryText: "text-[#00FF88]",
  },
  blue: {
    activeNav:   "bg-blue-500/10 text-blue-400 border-l-2 border-blue-500",
    primaryBg:   "bg-blue-500/[0.08] border-blue-500/30 hover:bg-blue-500/[0.15]",
    primaryIcon: "bg-blue-500/20",
    primaryText: "text-blue-400",
  },
};

// ── QuickDeployAction ─────────────────────────────────────────────────────────
export function QuickDeployAction({ icon: Icon, label, description, onClick, variant = "default", accent = "gold" }) {
  const a = ACCENT[accent] || ACCENT.gold;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-4 p-4 border transition-all text-left ${
        variant === "primary"
          ? `${a.primaryBg}`
          : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.15]"
      }`}
    >
      <div className={`w-10 h-10 flex items-center justify-center ${
        variant === "primary" ? a.primaryIcon : "bg-white/[0.04]"
      }`}>
        <Icon className={`w-5 h-5 ${variant === "primary" ? a.primaryText : "text-white/50"}`} />
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${variant === "primary" ? a.primaryText : "text-white"}`}>{label}</p>
        <p className="text-[10px] text-white/40 mt-0.5">{description}</p>
      </div>
      <ArrowRight className={`w-4 h-4 ${variant === "primary" ? a.primaryText : "text-white/30"}`} />
    </button>
  );
}

// ── PortalEmptyState ──────────────────────────────────────────────────────────
export function PortalEmptyState({ icon: Icon, title, desc, action, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 bg-white/[0.01] border border-white/[0.05] rounded-xl">
      <div className="w-14 h-14 bg-[#D4AF37]/[0.06] rounded-xl flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-[#D4AF37]/50" />
      </div>
      <p className="text-white/40 text-sm font-medium">{title}</p>
      {desc && <p className="text-white/20 text-xs mt-1">{desc}</p>}
      {action && (
        <button
          onClick={onAction}
          className="mt-4 text-xs px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded hover:bg-[#D4AF37]/20 transition-all"
        >
          {action}
        </button>
      )}
    </div>
  );
}

// ── ViewHeader ────────────────────────────────────────────────────────────────
export function ViewHeader({ title, subtitle, action, onAction, count, actionTestId }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-white/40 text-sm mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">
        {count !== undefined && (
          <span className="text-xs px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] text-white/50 rounded">
            {count} {count === 1 ? "item" : "items"}
          </span>
        )}
        {action && (
          <button
            onClick={onAction}
            data-testid={actionTestId}
            className="text-xs px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded hover:bg-[#D4AF37]/20 transition-all flex items-center gap-1.5"
          >
            {action} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── PortalRoleSwitcher ────────────────────────────────────────────────────────
/**
 * Shows a row of portal role buttons so multi-role users can switch portals.
 * Pass `currentRole` to exclude the current portal from the list.
 * Pass `userRoles` array from user.extra_roles (or [] to show all for admins).
 */
export function PortalRoleSwitcher({ currentRole, userRoles = [], isAdmin = false, collapsed = false }) {
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(null);

  const roles = isAdmin
    ? Object.keys(PORTAL_SWITCHER_ROLES).filter(r => r !== currentRole)
    : userRoles.filter(r => r !== currentRole && PORTAL_SWITCHER_ROLES[r]);

  const handleSwitch = useCallback(async (role) => {
    setSwitching(role);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/portal-token/${role}`,
        {},
        { withCredentials: true }
      );
      navigate(res.data.redirect || PORTAL_SWITCHER_ROLES[role]?.path || "/dashboard");
    } catch {
      navigate("/upgrade");
    } finally {
      setSwitching(null);
    }
  }, [navigate]);

  if (roles.length === 0) return null;

  return (
    <div className="px-2 pb-2">
      {!collapsed && (
        <p className="text-[9px] font-bold uppercase tracking-wider text-white/25 px-1 mb-1.5">Switch Portal</p>
      )}
      <div className="space-y-1">
        {roles.map(role => {
          const cfg = PORTAL_SWITCHER_ROLES[role];
          if (!cfg) return null;
          const Icon = cfg.icon;
          const isSwitching = switching === role;
          return (
            <button
              key={role}
              onClick={() => handleSwitch(role)}
              disabled={isSwitching}
              data-testid={`portal-switch-${role}`}
              title={collapsed ? cfg.label : undefined}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 border ${cfg.border} ${cfg.bg} rounded transition-all hover:opacity-90 ${collapsed ? "justify-center" : ""}`}
            >
              {isSwitching
                ? <RefreshCw className={`w-3.5 h-3.5 animate-spin ${cfg.color} flex-shrink-0`} />
                : <Icon className={`w-3.5 h-3.5 ${cfg.color} flex-shrink-0`} />
              }
              {!collapsed && <span className={`text-[11px] font-medium ${cfg.color}`}>{cfg.label}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Props:
 *   navSections   — Array<{ title: string, items: Array<{ icon, label, view }> }>
 *   activeView    — string
 *   setActiveView — fn
 *   homeLink      — string  (e.g. "/contractor")
 *   portal        — string  (e.g. "Contractor")
 *   entity        — object  (the lender/contractor/realtor/title profile)
 *   entityInitial — string  (fallback first letter)
 *   entitySubtitle— string  (e.g. "General Contractor")
 *   accent        — "gold" | "purple" | "emerald" | "blue"
 *   onLogout      — fn
 *   notifyCounts  — object  { [viewKey]: number } — optional badge counts
 */
export function PortalSidebar({
  navSections,
  activeView,
  setActiveView,
  homeLink,
  portal,
  entity,
  entityInitial = "?",
  entitySubtitle = "",
  accent = "gold",
  onLogout,
  notifyCounts = {},
  currentRole,
  userRoles = [],
  isAdmin = false,
}) {
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  const navigate = useNavigate();
  const a = ACCENT[accent] || ACCENT.gold;

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-[#0A0A0A]/80 backdrop-blur-sm border-r border-white/[0.06] transition-all z-40 flex flex-col ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between flex-shrink-0">
        {!collapsed ? (
          <Link to={homeLink} className="flex items-center">
            <DealForgeLogo size="sm" showTM={true} portal={portal} />
          </Link>
        ) : (
          <Link to={homeLink} className="flex items-center justify-center">
            <DealForgeIcon className="text-lg" />
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-white/50 hover:text-white p-1"
          data-testid={`${portal?.toLowerCase()}-sidebar-toggle`}
        >
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-2 flex-1 overflow-y-auto">
        {navSections.map((section, sIdx) => (
          <div key={sIdx} className="mb-4">
            {!collapsed && (
              <p className="px-3 py-2 text-[9px] text-white/30 uppercase tracking-wider font-semibold">
                {section.title}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = !item.url && activeView === item.view;
                const badgeCount = notifyCounts[item.view];
                return (
                  <button
                    key={item.url || item.view}
                    onClick={() => item.url ? navigate(item.url) : setActiveView(item.view)}
                    data-testid={`nav-${item.view || item.url?.replace('/', '')}`}
                    className={`w-full relative flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                      isActive ? a.activeNav : "text-white/50 hover:text-white hover:bg-white/[0.02]"
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-sm text-left flex-1">{item.label}</span>
                    )}
                    {badgeCount > 0 && !collapsed && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#D4AF37] text-black min-w-[18px] text-center leading-tight">
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                    {badgeCount > 0 && collapsed && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#D4AF37]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom links section */}
      <div className="flex-shrink-0">
        {/* Back to Investor Dashboard */}
        <div className="px-2 pb-1 border-t border-white/[0.04] pt-2">
          <Link
            to="/dashboard"
            data-testid="portal-back-to-investor"
            className="w-full flex items-center gap-3 px-3 py-2 rounded transition-all duration-300 border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] group"
          >
            <LayoutDashboard className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="text-[12px] text-white/60 flex-1 font-medium group-hover:text-white">Investor Dashboard</span>
                <ArrowRight className="w-3 h-3 text-white/20 group-hover:text-white/60 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </Link>
        </div>

        {/* Network Hub */}
        <div className="px-2 pb-1">
          <Link
            to="/network"
            data-testid="portal-network-hub-link"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] hover:bg-[#D4AF37]/10 group`}
          >
            <Globe className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="text-sm text-[#D4AF37] flex-1 font-medium">Network Hub</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#D4AF37]/50 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </Link>
        </div>

        {/* Role switcher for multi-role users */}
        <PortalRoleSwitcher
          currentRole={currentRole}
          userRoles={userRoles}
          isAdmin={isAdmin}
          collapsed={collapsed}
        />

        {/* User footer */}
        {!collapsed && (
          <div className="p-4 border-t border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                <span className={`font-bold ${a.primaryText}`}>
                  {entity?.company_name?.charAt(0) || entityInitial}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{entity?.company_name || portal}</p>
                <p className="text-white/40 text-xs truncate">{entitySubtitle}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.06] hover:border-red-500/50 transition-all duration-300"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
